import { useRef } from 'react';
import { WebSDKspeaker } from '../utils/webSDKspeakerClass';
import {
  createRelaySession,
  getMicStream,
  getSysStream,
  float32ToInt16,
  downsampleFloat32,
  buildFirCoefficients,
  sendRelayChunk,
  createResamplePhase,
  FINAL_OUTPUT_RATE,
  SpeakerCredentials,
} from '../services/speakerRelayService';

const RELAY_BACKEND = process.env.REACT_APP_BASE_URL || 'localhost:5000';

/**
 * 마이크 입력 게인. Windows 마이크 레벨·부스트가 과하게 설정된 환경에서
 * 원신호가 풀스케일(±1.0)을 넘어 하드 클리핑되는 사례가 잦아 헤드룸을 확보한다.
 */
const MIC_INPUT_GAIN = 0.5;

/** 합산 후 피크 억제용 리미터 설정 (DynamicsCompressorNode 를 리미터로 사용). */
const LIMITER_THRESHOLD_DB = -6;
const LIMITER_RATIO = 20;
const LIMITER_ATTACK_SEC = 0.003;
const LIMITER_RELEASE_SEC = 0.25;

/** 채임 원본 바이트 캐시 (URL → ArrayBuffer). */
const chimeBytesCache = new Map<string, Promise<ArrayBuffer>>();

/**
 * 채임 파일을 미리 받아 캐시한다. 모달 진입 시 호출해두면
 * 방송 중에는 네트워크 지연 없이 곧바로 디코딩·송출할 수 있다.
 * 특히 종료음은 로컬 재생이 끝나기 전에 relay 송출까지 마쳐야 하므로 선행 로드가 중요하다.
 */
export function prefetchChime(url: string): Promise<ArrayBuffer> {
  const cached = chimeBytesCache.get(url);
  if (cached) return cached;

  const pending = fetch(url, { credentials: 'omit' }).then((res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.arrayBuffer();
  });
  // 실패한 Promise 를 캐시에 남기면 이후 재시도가 영구히 막히므로 제거한다
  pending.catch(() => chimeBytesCache.delete(url));
  chimeBytesCache.set(url, pending);
  return pending;
}

export function useSpeakerRelay() {
  const relaySessionIdRef = useRef<string | null>(null);
  const browserAudioAbortRef = useRef<AbortController | null>(null);
  const browserAudioContextRef = useRef<AudioContext | null>(null);
  // 소스가 연결되는 입력 버스. 실제 캡처 목적지 앞단이므로 구체 타입이 아닌 AudioNode 로 둔다.
  const browserMixBusRef = useRef<AudioNode | null>(null);
  const browserMicStreamRef = useRef<MediaStream | null>(null);
  const browserSysStreamRef = useRef<MediaStream | null>(null);

  /**
   * Creates a relay session on the backend, then starts the WebVideoCtrl plugin
   * relay for the first connected speaker. Sets all speakers to status 2 (방송중).
   * Returns the new session ID.
   */
  const startPluginRelay = async (connectedSpeakers: WebSDKspeaker[]): Promise<string> => {
    const credentials: SpeakerCredentials[] = connectedSpeakers.map((sp) => ({
      ip: sp.getData().ip,
      port: sp.getData().port,
      id: sp.getData().id,
      pwd: sp.getData().pwd,
    }));

    const sessionId = await createRelaySession(credentials);
    relaySessionIdRef.current = sessionId;

    await connectedSpeakers[0].startRelay(sessionId, RELAY_BACKEND);
    connectedSpeakers.forEach((sp) => sp.setStatus(2));
    return sessionId;
  };

  /**
   * Captures browser mic + system audio (Stereo Mix, if available),
   * mixes them via Web Audio API, and streams G.711 µ-law chunks to the relay.
   */
  const startAudioCapture = async (sessionId: string): Promise<void> => {
    const micStream = await getMicStream();
    browserMicStreamRef.current = micStream;

    const sysStream = await getSysStream();
    browserSysStreamRef.current = sysStream;

    const audioContext = new AudioContext();
    // AudioContext created after multiple awaits may start in "suspended" state
    // even when triggered by a user gesture. Resume explicitly so the
    // ScriptProcessor fires from the first audio chunk.
    await audioContext.resume();
    browserAudioContextRef.current = audioContext;

    // 마이크·시스템·채임·음원이 모두 모이는 입력 버스.
    // playChime / routeAudioElementThroughRelay 가 connect 하는 대상이다.
    const inputBus = audioContext.createGain();

    // 합산 결과가 ±1.0 을 넘으면 float32ToInt16 이 ±32767 로 잘라내며(하드 클리핑)
    // 고차 하모닉 왜곡이 생겨 지직거림으로 들린다. 컴프레서를 리미터 설정으로 걸어 피크를 억제한다.
    const limiter = audioContext.createDynamicsCompressor();
    limiter.threshold.value = LIMITER_THRESHOLD_DB;
    limiter.knee.value = 0; // hard knee — 임계점 위를 곧바로 누른다
    limiter.ratio.value = LIMITER_RATIO;
    limiter.attack.value = LIMITER_ATTACK_SEC;
    limiter.release.value = LIMITER_RELEASE_SEC;

    const captureDest = audioContext.createMediaStreamDestination();
    inputBus.connect(limiter);
    limiter.connect(captureDest);
    browserMixBusRef.current = inputBus;

    // Windows 마이크 입력 레벨/부스트가 과하게 설정된 환경이 흔해
    // 마이크만 개별 게인으로 낮춰 헤드룸을 확보한다.
    const micGain = audioContext.createGain();
    micGain.gain.value = MIC_INPUT_GAIN;
    audioContext.createMediaStreamSource(micStream).connect(micGain);
    micGain.connect(inputBus);

    if (sysStream) audioContext.createMediaStreamSource(sysStream).connect(inputBus);

    // 1024 buffer fires every ~21ms at 48kHz — small enough to send 20ms chunks
    // without accumulation lag, but large enough not to drop callbacks.
    const processor = audioContext.createScriptProcessor(1024, 1, 1);
    audioContext.createMediaStreamSource(captureDest.stream).connect(processor);

    const abortCtrl = new AbortController();
    browserAudioAbortRef.current = abortCtrl;

    const inputRate = audioContext.sampleRate;
    // Downsample to 16 kHz (3:1 from 48 kHz) before sending.
    // G.711 encoding is done server-side, so we send raw PCM Int16 at 16 kHz.
    // Sending at 16 kHz (vs 8 kHz) preserves the full voice band up to 8 kHz
    // and lets the backend apply a simple 2:1 decimation — far less aliasing
    // than doing 6:1 in JavaScript.
    const targetRate = 16000;
    const chunkSamples = targetRate / 50; // 20 ms = 320 samples
    const pending: number[] = [];

    // FIR coefficients and delay line are created once per session and
    // reused across every onaudioprocess call to avoid buffer-boundary artifacts.
    // 컷오프 기준은 전송 레이트(16 kHz)가 아니라 최종 G.711 출력(8 kHz)이다.
    // 백엔드의 2:1 데시메이션이 2-tap 평균이라 4 kHz 이상을 제대로 걸러내지 못하므로
    // 여기서 3 500 Hz 로 잘라 보내야 에일리어싱(지직거림)이 생기지 않는다.
    const firCoeffs = buildFirCoefficients(inputRate, FINAL_OUTPUT_RATE);
    const delayLine = new Float32Array(firCoeffs.length - 1);
    // 버퍼 길이(1024)가 데시메이션 비율(3)의 배수가 아니라 매 콜백마다 위상이 어긋난다.
    // 이 상태를 호출 간에 이어주지 않으면 콜백 경계마다 출력 간격이 한 샘플 벌어져
    // 21.33ms 주기의 클릭이 발생한다.
    const resamplePhase = createResamplePhase();

    // HTTP 는 요청 간 도착 순서를 보장하지 않는다. 20ms 청크를 병렬로 발사하면
    // 순서가 뒤바뀐 채 장비에 이어붙어 파형이 깨지므로, 이전 전송이 끝난 뒤 다음을 보낸다.
    let sendChain: Promise<void> = Promise.resolve();

    processor.onaudioprocess = (e: AudioProcessingEvent) => {
      if (abortCtrl.signal.aborted) return;
      const raw = e.inputBuffer.getChannelData(0);

      const downsampled = downsampleFloat32(raw, inputRate, targetRate, firCoeffs, delayLine, resamplePhase);
      for (let i = 0; i < downsampled.length; i += 1) pending.push(downsampled[i]);

      while (pending.length >= chunkSamples) {
        const chunk = new Float32Array(pending.splice(0, chunkSamples));
        const pcmData = float32ToInt16(chunk);
        // catch 를 체인 안에 두어 한 청크가 실패해도 이후 전송이 멈추지 않게 한다.
        sendChain = sendChain
          .then(() => sendRelayChunk(sessionId, pcmData.buffer as ArrayBuffer, abortCtrl.signal))
          .catch((err) => {
            if (err.name !== 'AbortError') console.error('[relay audio]', err);
          });
      }
    };

    processor.connect(audioContext.destination);
  };

  /**
   * Tears down the Web Audio API graph and mic stream.
   * The sys stream (Stereo Mix) is intentionally kept alive via persistedSysStream
   * so the next broadcast reuses it without showing a dialog again.
   */
  const stopAudioCapture = (): void => {
    browserMixBusRef.current = null;
    if (browserAudioAbortRef.current) {
      browserAudioAbortRef.current.abort();
      browserAudioAbortRef.current = null;
    }
    if (browserAudioContextRef.current) {
      browserAudioContextRef.current.close().catch(() => {});
      browserAudioContextRef.current = null;
    }
    if (browserMicStreamRef.current) {
      browserMicStreamRef.current.getTracks().forEach((track) => track.stop());
      browserMicStreamRef.current = null;
    }
  };

  /**
   * Stops the plugin relay and resets all speaker statuses to 1 (방송가능).
   * The plugin internally sends a close signal to the relay backend.
   */
  const stopPluginRelay = async (broadcastingSpeakers: WebSDKspeaker[]): Promise<void> => {
    const first = broadcastingSpeakers[0];
    if (first) await first.stopPluginOnly();
    broadcastingSpeakers.forEach((sp) => sp.setStatus(1));
    relaySessionIdRef.current = null;
  };

  /**
   * Decodes a chime audio file and plays it through the Web Audio mix bus so it
   * is encoded and streamed to the speaker.
   * Uses AudioBufferSourceNode instead of createMediaElementSource because a
   * media element can only be routed once and the element is reused across broadcasts.
   * Local playback is handled separately by the caller's Audio element.
   *
   * 반환 Promise 는 relay 로 나가는 재생이 끝난 뒤 resolve 된다.
   * 종료음처럼 재생 직후 relay 를 내려야 하는 경우 반드시 이 Promise 를 기다려야
   * 스피커 쪽에서 소리가 잘리지 않는다.
   */
  const playChime = async (url: string): Promise<void> => {
    const ctx = browserAudioContextRef.current;
    const bus = browserMixBusRef.current;
    if (!ctx || !bus) return;
    // await 사이에 방송이 종료되면 state 가 'closed' 로 바뀌지만
    // TS 는 한 번 좁힌 타입을 되돌리지 않으므로 매번 새로 읽는다
    const isCtxClosed = () => (ctx.state as AudioContextState) === 'closed';

    try {
      if (ctx.state !== 'running') await ctx.resume();
      const bytes = await prefetchChime(url);
      // 방송이 이미 종료됐다면 디코딩/노드 생성이 예외를 던지므로 조용히 빠져나간다
      if (isCtxClosed()) return;
      // decodeAudioData 는 넘겨받은 ArrayBuffer 를 detach 하므로 캐시본이 아닌 복사본을 전달한다
      const audioBuffer = await ctx.decodeAudioData(bytes.slice(0));
      if (isCtxClosed()) return;

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(bus);

      await new Promise<void>((resolve) => {
        // AudioContext 가 먼저 닫히면 onended 가 오지 않으므로 재생 길이 기반 타임아웃을 함께 건다
        const fallbackTimer = setTimeout(resolve, (audioBuffer.duration + 1) * 1000);
        source.onended = () => {
          clearTimeout(fallbackTimer);
          resolve();
        };
        source.start();
      });
    } catch (err) {
      console.error('[playChime]', err);
    }
  };

  /**
   * Routes an existing HTMLAudioElement through the Web Audio mix bus so its
   * audio is encoded and streamed to the relay. Also connects to the
   * AudioContext destination so local playback continues normally.
   * Must be called before audio.play() and only once per element.
   */
  const routeAudioElementThroughRelay = (audioEl: HTMLAudioElement): void => {
    const ctx = browserAudioContextRef.current;
    const bus = browserMixBusRef.current;
    if (!ctx || !bus) return;
    try {
      const source = ctx.createMediaElementSource(audioEl);
      source.connect(bus);
      source.connect(ctx.destination);
    } catch (err) {
      console.error('[routeAudioElementThroughRelay]', err);
    }
  };

  return {
    relaySessionIdRef,
    browserAudioContextRef,
    browserMixBusRef,
    startPluginRelay,
    startAudioCapture,
    stopAudioCapture,
    stopPluginRelay,
    playChime,
    routeAudioElementThroughRelay,
  };
}
